import { type RawMatch, detectSecrets } from './detectors.ts';
import { type CompiledRule, type Disposition, loadBaselineRules } from './rules.ts';

export type FindingLocation = {
  line: number; // 0-based index of the JSONL line
  jsonPointer: string; // RFC-6901 pointer to the string leaf, e.g. "/message/content/2/text"
};

export type Finding = {
  location: FindingLocation;
  start: number; // char offset into the DECODED leaf string, inclusive
  end: number; // exclusive
  ruleId: string;
  disposition: Disposition;
};

export type ScanResult = {
  findings: Finding[];
  unparsableLines: number[]; // 0-based indexes of lines that did not parse as JSON
};

// RFC-6901: escape `~` as `~0` and `/` as `~1` (order matters).
function escapePointerToken(token: string): string {
  return token.replaceAll('~', '~0').replaceAll('/', '~1');
}

function walk(
  node: unknown,
  pointer: string,
  line: number,
  rules: CompiledRule[],
  findings: Finding[],
): void {
  if (typeof node === 'string') {
    // Rules are already keyword-gated against the whole transcript, so run all
    // of them on every leaf — a secret can sit on a leaf that lacks the keyword.
    const matches: RawMatch[] = detectSecrets(node, rules, { skipKeywordPrefilter: true });
    for (const match of matches) {
      findings.push({
        location: { line, jsonPointer: pointer },
        start: match.start,
        end: match.end,
        ruleId: match.ruleId,
        disposition: match.disposition,
      });
    }
    return;
  }
  if (Array.isArray(node)) {
    node.forEach((child, index) => walk(child, `${pointer}/${index}`, line, rules, findings));
    return;
  }
  if (node && typeof node === 'object') {
    for (const [key, value] of Object.entries(node)) {
      walk(value, `${pointer}/${escapePointerToken(key)}`, line, rules, findings);
    }
  }
  // numbers, booleans, null: nothing to scan
}

/**
 * Scan a JSONL transcript for secrets. Parses each line, walks its string
 * **values** (not keys), and runs the detector engine on the **decoded**
 * value — so reported offsets index the logical string, never the escaped
 * JSON bytes (TAN-4341 redacts in decoded space). Lines that don't parse as
 * JSON are reported in `unparsableLines`; the gate (TAN-4341) decides what to
 * do with them. No secret bytes appear anywhere in the result.
 */
export function scanTranscript(jsonl: string, rules?: CompiledRule[]): ScanResult {
  // Keyword-prefilter once against the WHOLE transcript: a rule is a candidate
  // if any of its keywords appears anywhere in the file (or it has none). This
  // matches gitleaks' fragment-level gating and avoids the cross-leaf miss
  // where a keyword on one leaf would otherwise leave the same secret unscanned
  // on another.
  const allRules = rules ?? loadBaselineRules();
  const lowerAll = jsonl.toLowerCase();
  const candidateRules = allRules.filter(
    (rule) => rule.keywords.length === 0 || rule.keywords.some((kw) => lowerAll.includes(kw)),
  );

  const lines = jsonl.split('\n');
  // A trailing newline yields a final empty element — not a real (unparsable) line.
  if (lines.length > 0 && lines[lines.length - 1] === '') lines.pop();

  const findings: Finding[] = [];
  const unparsableLines: number[] = [];

  for (let line = 0; line < lines.length; line += 1) {
    const raw = lines[line] ?? '';
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      unparsableLines.push(line);
      continue;
    }
    walk(parsed, '', line, candidateRules, findings);
  }

  findings.sort(
    (a, b) =>
      a.location.line - b.location.line ||
      (a.location.jsonPointer < b.location.jsonPointer
        ? -1
        : a.location.jsonPointer > b.location.jsonPointer
          ? 1
          : 0) ||
      a.start - b.start,
  );

  return { findings, unparsableLines };
}
